import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { Field, TextArea } from '@/components/ui/Field';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from './ProjectsPage.module.css';

type DeliverabilityResponse = {
  mailboxes: {
    id: string;
    label: string;
    sent_today: number;
    sent_hour: number;
    daily_limit: number;
    hourly_limit: number;
    status: string;
    last_error: string | null;
    imap_last_sync_at: string | null;
    is_default: boolean;
  }[];
  compliance: {
    address_set: boolean;
    require_legal_basis: boolean;
    compliance_address: string | null;
  };
  dnsHints: { domain: string; spf: string; dkim: string; dmarc: string };
  unmatched_inbound: number;
};

export function DeliverabilityPage() {
  const { selectedProject, selectedProjectId } = useProjectContext();
  const qc = useQueryClient();

  const delQ = useQuery({
    queryKey: ['mail-deliverability', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<DeliverabilityResponse>(`/api/mail/deliverability?project_id=${selectedProjectId}`),
  });

  const saveCompliance = useMutation({
    mutationFn: (body: { compliance_address: string | null; mail_require_legal_basis: boolean }) =>
      apiFetch(`/api/projects/${selectedProjectId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mail-deliverability'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return (
    <ProjectGate requireSelection pageTitle="Deliverability">
      <h1 className={styles.title}>Deliverability</h1>
      <StateMessage title="CRM E">
        Mailbox-limieten, DNS-checklist en compliance voor {selectedProject?.name ?? 'project'}.
      </StateMessage>

      {delQ.isLoading ? (
        <LoadingState />
      ) : delQ.isError ? (
        <ErrorState message={(delQ.error as Error).message} />
      ) : delQ.data ? (
        <>
          {delQ.data.unmatched_inbound ? (
            <StateMessage title="Unmatched inbound">
              {delQ.data.unmatched_inbound} bericht(en) zonder lead — koppel ze in de inbox.
            </StateMessage>
          ) : null}

          <section className={styles.card}>
            <h2>Mailboxen</h2>
            <ul className={styles.list}>
              {delQ.data.mailboxes.map((m) => (
                <li key={m.id} className={styles.listItem}>
                  <div>
                    <strong>
                      {m.label}
                      {m.is_default ? ' (default)' : ''}
                    </strong>
                    <div className={styles.mutedSmall}>
                      Status: {m.status}
                      {m.last_error ? ` · ${m.last_error}` : ''}
                    </div>
                    <div className={styles.mutedSmall}>
                      Vandaag {m.sent_today}/{m.daily_limit} · Uur {m.sent_hour}/{m.hourly_limit}
                    </div>
                    {m.imap_last_sync_at ? (
                      <div className={styles.mutedSmall}>
                        IMAP sync: {new Date(m.imap_last_sync_at).toLocaleString('nl-NL')}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.card}>
            <h2>DNS (hints)</h2>
            <p className={styles.mutedSmall}>Domein: {delQ.data.dnsHints.domain}</p>
            <pre className={styles.keyBox}>{delQ.data.dnsHints.spf}</pre>
            <pre className={styles.keyBox}>{delQ.data.dnsHints.dkim}</pre>
            <pre className={styles.keyBox}>{delQ.data.dnsHints.dmarc}</pre>
          </section>

          <section className={styles.card}>
            <h2>Compliance footer</h2>
            <Field label="Bedrijfsgegevens (plain text in mail)">
              <TextArea
                rows={3}
                defaultValue={delQ.data.compliance.compliance_address ?? ''}
                key={delQ.data.compliance.compliance_address ?? 'empty'}
                onBlur={(e) =>
                  saveCompliance.mutate({
                    compliance_address: e.target.value.trim() || null,
                    mail_require_legal_basis: delQ.data!.compliance.require_legal_basis,
                  })
                }
              />
            </Field>
            <label className={styles.mutedSmall}>
              <input
                type="checkbox"
                defaultChecked={delQ.data.compliance.require_legal_basis}
                onChange={(e) =>
                  saveCompliance.mutate({
                    compliance_address: delQ.data!.compliance.compliance_address,
                    mail_require_legal_basis: e.target.checked,
                  })
                }
              />{' '}
              Blokkeer 1:1 mail zonder rechtsgrond op lead
            </label>
          </section>
        </>
      ) : null}
    </ProjectGate>
  );
}
